/**
 * Warning shown before the idle sign-out of a "remember me" unticked login.
 */

import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

interface IdleSignOutDialogProps {
  open: boolean;
  secondsLeft: number;
  onStay: () => void;
  onSignOut: () => void;
}

function formatCountdown(seconds: number): string {
  const s = Math.max(0, seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "1 minute 35 seconds": "1:35" can be read out as a time of day. */
function spokenCountdown(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const parts = [];
  if (m) parts.push(`${m} minute${m === 1 ? "" : "s"}`);
  if (s % 60 || !m) parts.push(`${s % 60} second${s % 60 === 1 ? "" : "s"}`);
  return parts.join(" ");
}

export const IdleSignOutDialog: React.FC<IdleSignOutDialogProps> = ({
  open,
  secondsLeft,
  onStay,
  onSignOut,
}) => (
  <Dialog
    open={open}
    maxWidth="xs"
    fullWidth
    onClose={(_event, reason) => {
      // A click on the backdrop isn't an answer; Escape means "stay".
      if (reason === "escapeKeyDown") onStay();
    }}
    aria-labelledby="idle-signout-title"
    aria-describedby="idle-signout-text"
    slotProps={{ paper: { role: "alertdialog" } }}
  >
    <DialogTitle id="idle-signout-title">Are you still there?</DialogTitle>
    <DialogContent>
      <DialogContentText id="idle-signout-text">
        You'll be signed out in{" "}
        <strong>
          <span aria-hidden="true">{formatCountdown(secondsLeft)}</span>
          <span className="sr-only">{spokenCountdown(secondsLeft)}</span>
        </strong>{" "}
        because there has been no activity for a while and "Remember me" was off when you
        signed in.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onSignOut}>Sign out</Button>
      <Button onClick={onStay} variant="contained" autoFocus>
        Stay signed in
      </Button>
    </DialogActions>
  </Dialog>
);
